import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Breadcrumb, SearchInput, FilterCount, Loading } from "../components/ui";
import { UserAuth } from "../context/AuthContext";

// Process tile - just name, like NameTile
const ProcessTile = ({ process }) => (
  <Link to={`/processes/${process.function}`} className="block h-full">
    <div className="bg-base-100 rounded-lg p-4 border border-base-content/10 hover:border-base-content/30 h-full flex items-center justify-center min-h-[80px] transition-colors">
      <span className="font-[Oswald] font-semibold uppercase tracking-tight -skew-x-6 text-[#555] text-sm text-center">
        {process.name}
      </span>
    </div>
  </Link>
);

// Section header component
const SectionHeader = ({ title, count }) => (
  <div className="mb-3">
    <span className="text-sm font-medium text-base-content/60">
      {title}
      {count !== undefined && <span className="ml-2 text-base-content/40">({count})</span>}
    </span>
  </div>
);

function AdminProcesses() {
  const { user } = UserAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [processes, setProcesses] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const host = process.env.REACT_APP_SERVER;

  // Group processes by type
  const groupedProcesses = {};
  processes.forEach(d => {
    const type = d.type || "Other";
    if (!groupedProcesses[type]) {
      groupedProcesses[type] = [];
    }
    groupedProcesses[type].push(d);
  });

  // Filter by search
  const filteredGroups = {};
  Object.keys(groupedProcesses).forEach(type => {
    const filtered = groupedProcesses[type].filter(p =>
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );
    if (filtered.length > 0) {
      filteredGroups[type] = filtered;
    }
  });

  useEffect(() => {
    if (user === null) {
      navigate("/signin");
    }
  }, [user, navigate]);

  useEffect(() => {
    setLoading(true);
    const fetchProcesses = async () => {
      try {
        const response = await fetch(`${host}/api/processes`);
        const processesData = await response.json();
        setProcesses(processesData.data || []);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchProcesses();
  }, [host]);

  if (!user?.email) {
    return null;
  }

  if (loading) {
    return <Loading size="lg" message="Loading processes..." fullScreen />;
  }

  const totalCount = Object.values(filteredGroups).flat().length;

  return (
    <div className="container mx-auto px-4 py-6">
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Admin", href: "/admin" },
          { label: "Processes" },
        ]}
        className="mb-4"
      />

      {/* Header Card */}
      <div className="bg-base-100 rounded-lg p-4 sm:p-5 mb-6 border border-base-content/10">
        <h1 className="text-2xl font-semibold text-base-content mb-4">Processes</h1>

        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search processes..."
          className="mb-4"
        />

        <div className="flex items-center justify-end">
          <FilterCount filtered={totalCount} total={processes.length} label="processes" />
        </div>
      </div>

      {/* Process Groups */}
      {Object.keys(filteredGroups).sort().map((type, idx) => (
        <div key={type} className={idx > 0 ? "mt-6" : ""}>
          <SectionHeader title={type} count={filteredGroups[type].length} />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {filteredGroups[type].map(process => (
              <ProcessTile key={process.function || process.name} process={process} />
            ))}
          </div>
        </div>
      ))}

      {/* No Results */}
      {Object.keys(filteredGroups).length === 0 && (
        <div className="text-center py-12 text-base-content/50">
          No processes found matching "{searchQuery}"
        </div>
      )}
    </div>
  );
}

export default AdminProcesses;
